import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { AlertTriangle, CheckCircle, Save, Loader2, Info } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

function calcFersMatch(empPct, salary) {
  const auto = salary * 0.01;
  const t1 = Math.min(empPct, 0.03) * 1.0 * salary;
  const t2 = Math.min(Math.max(empPct - 0.03, 0), 0.02) * 0.5 * salary;
  return { auto: auto / 12, match: (t1 + t2) / 12, total: (auto + t1 + t2) / 12, fullMatch: empPct >= 0.05 };
}

export const ContributionEditorPage = () => {
  const [salary, setSalary] = useState(0);
  const [age, setAge] = useState(35);
  const [dob, setDob] = useState('');
  const [ytdUsed, setYtdUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // IRS limits from API
  const [irsLimits, setIrsLimits] = useState(null);

  // User inputs
  const [rate, setRate] = useState(5);
  const [tradPct, setTradPct] = useState(70);

  // Dirty tracking
  const [initialRate, setInitialRate] = useState(5);
  const [initialTradPct, setInitialTradPct] = useState(70);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contribRes, profileRes, irsRes] = await Promise.allSettled([
        api.get('/tsp/participant/contributions'),
        api.get('/tsp/participant/profile'),
        api.get('/tsp/market/irs-limits/2026'),
      ]);

      if (contribRes.status === 'fulfilled') {
        const d = contribRes.value.data;
        setRate(d.rate ?? 5);
        setTradPct(d.traditional_pct ?? 70);
        setInitialRate(d.rate ?? 5);
        setInitialTradPct(d.traditional_pct ?? 70);
        setYtdUsed(d.ytd_amount ?? 0);
        setSalary(d.annual_salary ?? 85000);
      }

      if (profileRes.status === 'fulfilled') {
        const p = profileRes.value.data;
        setDob(p.date_of_birth || '');
        // Calculate age from dob
        if (p.date_of_birth) {
          const bd = new Date(p.date_of_birth);
          const today = new Date();
          let a = today.getFullYear() - bd.getFullYear();
          if (today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate())) a--;
          setAge(a);
        }
      }

      if (irsRes.status === 'fulfilled') {
        setIrsLimits(irsRes.value.data);
      }
    } catch {} finally { setLoading(false); }
  };

  const rothPct = 100 - tradPct;
  const empPctDecimal = rate / 100;

  // Derive limits from API or calculate
  const irsBase = irsLimits?.standard ?? 23500;
  const catchupStd = irsLimits?.catch_up_standard ?? 7500;
  const catchupEnh = irsLimits?.catch_up_enhanced ?? 11250;

  // Catch-up tier logic per spec:
  // Age 50-59 or 64+: standard catch-up +$7,500
  // Age 60-63: enhanced catch-up +$11,250 (SECURE 2.0)
  // Under 50: none
  let catchup = 0;
  let catchupTier = 'none';
  if (age >= 60 && age <= 63) {
    catchup = catchupEnh;
    catchupTier = 'enhanced_60_63';
  } else if (age >= 50) {
    catchup = catchupStd;
    catchupTier = 'standard_50plus';
  }
  const personalLimit = irsBase + catchup;

  const isDirty = rate !== initialRate || tradPct !== initialTradPct;

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors = [];
    if (Math.abs((tradPct + rothPct) - 100) > 0.01) {
      errors.push('Traditional + Roth must equal 100%');
    }
    const annualContrib = salary * empPctDecimal;
    if (annualContrib > personalLimit) {
      const maxRate = Math.floor((personalLimit / salary) * 1000) / 10;
      errors.push(`Annual contribution of ${fmtFull(annualContrib)} exceeds § 402(g) limit of ${fmtFull(personalLimit)}. Maximum rate: ${maxRate}%`);
    }
    return errors;
  }, [rate, tradPct, rothPct, salary, empPctDecimal, personalLimit]);

  const canSave = isDirty && validationErrors.length === 0;

  const calc = useMemo(() => {
    const monthlyGross = salary / 12;
    const monthlyContrib = monthlyGross * empPctDecimal;
    const monthlyTrad = monthlyContrib * (tradPct / 100);
    const monthlyRoth = monthlyContrib * (rothPct / 100);
    const match = calcFersMatch(empPctDecimal, salary);
    const now = new Date();
    const monthsRemaining = 12 - now.getMonth();
    const projectedYtd = ytdUsed + (monthlyContrib * monthsRemaining);
    const remaining = Math.max(personalLimit - projectedYtd, 0);
    const overLimit = projectedYtd > personalLimit;
    return {
      monthlyContrib, monthlyTrad, monthlyRoth,
      match, projectedYtd, remaining, overLimit,
      annualContrib: salary * empPctDecimal,
      annualAgency: match.total * 12,
    };
  }, [rate, tradPct, salary, ytdUsed, empPctDecimal, rothPct, personalLimit]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const res = await api.put('/tsp/participant/contributions', {
        rate,
        traditional_pct: tradPct,
        roth_pct: rothPct,
      });
      setSaveSuccess(`Elections saved. Effective: ${res.data.effective_date}. Annual contribution: ${fmtFull(res.data.annual_contribution)}`);
      setInitialRate(rate);
      setInitialTradPct(tradPct);
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Failed to save contribution elections');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="panel p-6 animate-pulse"><div className="h-6 bg-gray-100 rounded w-48 mb-4" /><div className="h-40 bg-gray-50 rounded" /></div>;
  }

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] font-mono focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  return (
    <div className="space-y-4" data-testid="contribution-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Contribution Rate Editor</h1>
        <button
          onClick={handleSave}
          disabled={!canSave || saving}
          data-testid="save-contributions-btn"
          className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded transition-colors ${
            canSave ? 'bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white' : 'bg-[#E2E5E9] text-[#9CA3AF] cursor-not-allowed'
          }`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving...' : 'Save Elections'}
        </button>
      </div>

      {/* Save feedback */}
      {saveSuccess && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-4 py-3" data-testid="save-success">
          <CheckCircle size={14} className="text-emerald-600" />
          <p className="text-xs text-emerald-700">{saveSuccess}</p>
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="save-error">
          <AlertTriangle size={14} className="text-red-600" />
          <p className="text-xs text-red-700">{saveError}</p>
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded px-4 py-3 space-y-1" data-testid="validation-errors">
          {validationErrors.map((e, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertTriangle size={12} className="text-red-600 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700">{e}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input Section */}
        <div className="panel p-5" data-testid="contribution-inputs">
          <span className="overline mb-4 block">Your Elections</span>

          {/* Rate slider */}
          <div className="mb-5">
            <label className="block text-xs font-medium text-[#1B2A4A] mb-2">Employee Contribution Rate</label>
            <div className="flex items-center gap-4">
              <input type="range" min={0} max={100} step={0.5} value={rate} onChange={e => { setRate(parseFloat(e.target.value)); setSaveSuccess(''); setSaveError(''); }}
                className="flex-1" data-testid="rate-slider" />
              <div className="w-20">
                <input type="number" min={0} max={100} step={0.5} value={rate}
                  onChange={e => { setRate(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))); setSaveSuccess(''); setSaveError(''); }}
                  className={`${inputClass} text-center`} data-testid="rate-input" />
              </div>
              <span className="text-sm font-mono font-semibold text-[#1B2A4A]">%</span>
            </div>
            {rate < 5 && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                <AlertTriangle size={12} /><span>Below 5%: not capturing the full FERS match</span>
              </div>
            )}
            {rate >= 5 && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600">
                <CheckCircle size={12} /><span>Capturing full FERS match at {rate}%</span>
              </div>
            )}
          </div>

          {/* Traditional / Roth split */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-[#1B2A4A] mb-2">Traditional / Roth Split</label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                  <span>Traditional (Pre-Tax)</span>
                  <span className="font-mono font-medium text-[#1B2A4A]">{tradPct}%</span>
                </div>
                <input type="range" min={0} max={100} step={5} value={tradPct}
                  onChange={e => { setTradPct(parseInt(e.target.value)); setSaveSuccess(''); setSaveError(''); }}
                  className="w-full" data-testid="trad-split-slider" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                  <span>Roth (Post-Tax)</span>
                  <span className="font-mono font-medium text-[#1B2A4A]">{rothPct}%</span>
                </div>
                <input type="range" min={0} max={100} step={5} value={rothPct}
                  onChange={e => { setTradPct(100 - parseInt(e.target.value)); setSaveSuccess(''); setSaveError(''); }}
                  className="w-full" data-testid="roth-split-slider" />
              </div>
            </div>
            <div className="mt-2 flex h-2.5 rounded-sm overflow-hidden">
              <div className="bg-[#1B2A4A] transition-all" style={{ width: `${tradPct}%` }} />
              <div className="bg-emerald-500 transition-all" style={{ width: `${rothPct}%` }} />
            </div>
            <div className="flex justify-between text-[9px] text-[#9CA3AF] mt-1 font-mono">
              <span>Traditional {tradPct}%</span>
              <span>Roth {rothPct}%</span>
            </div>
          </div>

          {/* Catch-up tier display */}
          <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3 mb-4" data-testid="catchup-info">
            <div className="flex items-start gap-2">
              <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">
                  {catchupTier === 'enhanced_60_63'
                    ? `SECURE 2.0 Enhanced Catch-Up (Age 60-63): +${fmtFull(catchup)}`
                    : catchupTier === 'standard_50plus'
                    ? `Standard Catch-Up (Age 50+): +${fmtFull(catchup)}`
                    : `Standard Limit Only (Age ${age}, under 50)`}
                </p>
                <p>Your applicable limit: <span className="font-mono font-semibold">{fmtFull(personalLimit)}</span></p>
                <div className="mt-1.5 grid grid-cols-2 gap-x-4 text-[10px] text-blue-600">
                  <span>Standard deferral: {fmtFull(irsBase)}</span>
                  <span>Catch-up 50-59/64+: +{fmtFull(catchupStd)}</span>
                  <span>Enhanced 60-63: +{fmtFull(catchupEnh)}</span>
                  <span>Total additions: {fmtFull(irsLimits?.total_additions ?? 70000)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Salary display */}
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-4 py-2.5 text-xs">
            <div className="flex justify-between"><span className="text-[#6B7280]">Annual Salary</span><span className="font-mono font-medium text-[#1B2A4A]">{fmtFull(salary)}</span></div>
          </div>
        </div>

        {/* Live Calculations */}
        <div className="panel p-5" data-testid="contribution-calculations">
          <span className="overline mb-4 block">Live Calculations</span>
          <div className="space-y-3">
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4">
              <span className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Monthly Contribution</span>
              <div className="font-mono text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="monthly-amount">{fmtFull(calc.monthlyContrib)}</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">Traditional</span><span className="font-mono text-[#1B2A4A]">{fmtFull(calc.monthlyTrad)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">Roth</span><span className="font-mono text-[#1B2A4A]">{fmtFull(calc.monthlyRoth)}</span></div>
              </div>
            </div>

            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4" data-testid="agency-match-calc">
              <span className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Monthly Agency Match (FERS)</span>
              <div className="font-mono text-xl font-semibold text-[#1B2A4A] mt-1">{fmtFull(calc.match.total)}</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">Auto 1%</span><span className="font-mono">{fmtFull(calc.match.auto)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">Match</span><span className="font-mono">{fmtFull(calc.match.match)}</span></div>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                {calc.match.fullMatch ?
                  <><CheckCircle size={12} className="text-emerald-600" /><span className="text-emerald-700 font-medium">Full match captured</span></> :
                  <><AlertTriangle size={12} className="text-amber-500" /><span className="text-amber-700">Increase to 5% for full match</span></>
                }
              </div>
            </div>

            <div className={`rounded p-4 ${calc.overLimit ? 'bg-red-50 border border-red-200' : 'bg-[#F8F9FA] border border-[#E2E5E9]'}`} data-testid="ytd-projection">
              <span className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Projected YTD by Dec 31</span>
              <div className={`font-mono text-xl font-semibold mt-1 ${calc.overLimit ? 'text-red-600' : 'text-[#1B2A4A]'}`} data-testid="projected-ytd">
                {fmtFull(calc.projectedYtd)}
              </div>
              <div className="mt-2 w-full h-2.5 bg-[#E2E5E9] rounded-sm overflow-hidden">
                <div className={`h-full rounded-sm transition-all ${calc.overLimit ? 'bg-red-500' : 'bg-[#1B2A4A]'}`}
                  style={{ width: `${Math.min((calc.projectedYtd / personalLimit) * 100, 100)}%` }} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">IRS Limit (§ 402(g))</span><span className="font-mono font-medium">{fmtFull(personalLimit)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">Remaining Room</span><span className={`font-mono font-medium ${calc.remaining === 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtFull(calc.remaining)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#9CA3AF]">YTD Used</span><span className="font-mono">{fmtFull(ytdUsed)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#E2E5E9]">
                  <span className="text-[#9CA3AF]">Catch-Up</span>
                  <span className="font-mono">{catchup > 0 ? `+${fmtFull(catchup)} (${catchupTier === 'enhanced_60_63' ? '60-63' : '50+'})` : 'N/A'}</span>
                </div>
              </div>
              {calc.overLimit && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-700">
                  <AlertTriangle size={12} /><span>Projected contributions exceed IRS limit. Reduce rate to save.</span>
                </div>
              )}
            </div>

            <div className="bg-[#1B2A4A] rounded p-4 text-white" data-testid="annual-summary">
              <span className="text-[10px] font-medium text-[#8B95A5] uppercase tracking-wider">Annual Total Retirement Savings</span>
              <div className="font-mono text-2xl font-bold mt-1" data-testid="annual-total">{fmtFull(calc.annualContrib + calc.annualAgency)}</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-[#B8C5D8]">
                <div className="flex justify-between py-1 border-b border-[#2D3F5F]"><span>Your Contribution</span><span className="font-mono text-white">{fmtFull(calc.annualContrib)}</span></div>
                <div className="flex justify-between py-1 border-b border-[#2D3F5F]"><span>Agency Total</span><span className="font-mono text-white">{fmtFull(calc.annualAgency)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-[#9CA3AF] text-center leading-relaxed">
        FERS Match: 5 U.S.C. § 8432(c) · IRS Deferral: 26 U.S.C. § 402(g) · SECURE 2.0 Enhanced Catch-Up: ages 60-63 · Total Additions: 26 U.S.C. § 415(c)
      </div>
    </div>
  );
};
